import { BranchNotFoundError, DirtyHeadError, EntityAlreadyExistsError, EntityNotFoundError, GarbageCollectionPlanStaleError, PersistenceError, RevisionNotFoundError, SchemaCompatibilityError, ValidationError } from "./errors.js";
import { hashState, stableStringify } from "./hash.js";
import { cloneJson } from "./json.js";
import { migrateState } from "./migrations.js";
import { resolveGraphIdentity } from "./schema-fingerprint.js";
export function createRepository(options) {
    let activeBranch = options.defaultBranch ?? "main";
    const defaultBranch = options.defaultBranch ?? "main";
    const graphVersion = options.graphVersion ?? "1";
    const graphIdentity = resolveGraphIdentity({
        graph: options.graph,
        graphVersion,
        ...(options.schemaFingerprint === undefined
            ? {}
            : { schemaFingerprint: options.schemaFingerprint }),
        ...(options.schemaFingerprintAlgorithm === undefined
            ? {}
            : { schemaFingerprintAlgorithm: options.schemaFingerprintAlgorithm })
    });
    const migrations = options.migrations ?? [];
    function resolveBranch(branch) {
        return branch ?? activeBranch;
    }
    function validateState(input) {
        return options.graph.validateState(input);
    }
    function resolveMigrationTarget(migrationOptions) {
        const requestedTarget = migrationOptions?.migrateTo;
        if (requestedTarget !== undefined) {
            return requestedTarget;
        }
        if ("migration" in (migrationOptions ?? {}) &&
            migrationOptions.migration === "latest") {
            return "current";
        }
        return null;
    }
    function assertCurrentTarget(targetGraphVersion) {
        if (targetGraphVersion !== graphVersion) {
            throw new ValidationError("Migration target does not match repository graph.", [
                {
                    path: ["graphVersion"],
                    message: `Repository can validate "${graphVersion}", not "${targetGraphVersion}".`
                }
            ]);
        }
    }
    function resolveTargetGraphVersion(target) {
        return target === "current" || target === "strict" ? graphVersion : target;
    }
    function hasMigrationPath(from, to) {
        if (from === to) {
            return true;
        }
        const visited = new Set([from]);
        const queue = [from];
        while (queue.length > 0) {
            const current = queue.shift();
            if (current === undefined) {
                break;
            }
            for (const migration of migrations) {
                if (migration.from !== current || visited.has(migration.to)) {
                    continue;
                }
                if (migration.to === to) {
                    return true;
                }
                visited.add(migration.to);
                queue.push(migration.to);
            }
        }
        return false;
    }
    async function readHeadGraphIdentity(head) {
        const sourceRevision = head.headRevision ?? head.baseRevision;
        if (sourceRevision !== null) {
            const storedRevision = await options.persistence.readRevision({
                repoId: options.repoId,
                revision: sourceRevision
            });
            if (storedRevision === null) {
                throw new RevisionNotFoundError(`Revision "${sourceRevision}" was not found.`);
            }
            return {
                graphVersion: storedRevision.revision.graphVersion,
                schemaFingerprint: storedRevision.revision.schemaFingerprint,
                schemaFingerprintAlgorithm: storedRevision.revision.schemaFingerprintAlgorithm
            };
        }
        const repo = await options.persistence.getRepo({ repoId: options.repoId });
        if (repo === null) {
            throw new RevisionNotFoundError(`Repository "${options.repoId}" was not found.`);
        }
        return {
            graphVersion: repo.graphVersion,
            schemaFingerprint: repo.schemaFingerprint,
            schemaFingerprintAlgorithm: repo.schemaFingerprintAlgorithm
        };
    }
    async function validateHead(head, getHeadOptions) {
        let stateInput = head.state;
        const rawStateHash = await hashState(stateInput);
        if (rawStateHash !== head.stateHash) {
            throw new ValidationError("HEAD state hash does not match its state.", [
                {
                    path: ["stateHash"],
                    message: `Expected "${head.stateHash}", computed "${rawStateHash}".`
                }
            ]);
        }
        const targetGraphVersion = resolveMigrationTarget(getHeadOptions);
        if (targetGraphVersion === "raw") {
            return {
                ...head,
                state: cloneJson(stateInput)
            };
        }
        const sourceGraphIdentity = await readHeadGraphIdentity(head);
        if (targetGraphVersion === null ||
            targetGraphVersion === "strict") {
            if (sourceGraphIdentity.schemaFingerprint !== graphIdentity.schemaFingerprint) {
                throw new SchemaCompatibilityError(`HEAD schema fingerprint "${sourceGraphIdentity.schemaFingerprint}" is not compatible with current "${graphIdentity.schemaFingerprint}".`);
            }
        }
        else {
            const resolvedTargetGraphVersion = resolveTargetGraphVersion(targetGraphVersion);
            assertCurrentTarget(resolvedTargetGraphVersion);
            if (sourceGraphIdentity.graphVersion !== resolvedTargetGraphVersion) {
                stateInput = migrateState({
                    state: stateInput,
                    from: sourceGraphIdentity.graphVersion,
                    to: resolvedTargetGraphVersion,
                    migrations
                });
            }
        }
        const state = validateState(stateInput);
        const stateHash = await hashState(state);
        if (targetGraphVersion === null && stateHash !== head.stateHash) {
            throw new ValidationError("HEAD state hash does not match its state.", [
                {
                    path: ["stateHash"],
                    message: `Expected "${head.stateHash}", computed "${stateHash}".`
                }
            ]);
        }
        if (head.status === "clean" && head.headRevision === null) {
            throw new ValidationError("Clean HEAD must point to a revision.", [
                {
                    path: ["headRevision"],
                    message: "Clean HEAD must have a headRevision."
                }
            ]);
        }
        return {
            ...head,
            stateHash,
            state
        };
    }
    async function validateStoredRevision(revision, stateInput, readRevisionOptions) {
        let nextStateInput = stateInput;
        const rawStateHash = await hashState(stateInput);
        if (rawStateHash !== revision.stateHash) {
            throw new ValidationError("Revision state hash does not match its state.", [
                {
                    path: ["stateHash"],
                    message: `Expected "${revision.stateHash}", computed "${rawStateHash}".`
                }
            ]);
        }
        const targetGraphVersion = resolveMigrationTarget(readRevisionOptions);
        if (targetGraphVersion === "raw") {
            stableStringify(stateInput);
            return cloneJson(stateInput);
        }
        if ((targetGraphVersion === null || targetGraphVersion === "strict") &&
            revision.schemaFingerprint !== graphIdentity.schemaFingerprint) {
            throw new SchemaCompatibilityError(`Revision "${revision.revision}" schema fingerprint "${revision.schemaFingerprint}" is not compatible with current "${graphIdentity.schemaFingerprint}".`);
        }
        if (targetGraphVersion !== null && targetGraphVersion !== "strict") {
            const resolvedTargetGraphVersion = resolveTargetGraphVersion(targetGraphVersion);
            assertCurrentTarget(resolvedTargetGraphVersion);
            if (revision.graphVersion !== resolvedTargetGraphVersion) {
                nextStateInput = migrateState({
                    state: stateInput,
                    from: revision.graphVersion,
                    to: resolvedTargetGraphVersion,
                    migrations
                });
            }
        }
        const state = validateState(nextStateInput);
        return state;
    }
    async function writeUpdate(nextStateInput, updateOptions = {}) {
        const branchName = resolveBranch(updateOptions.branch);
        const state = validateState(nextStateInput);
        const stateHash = await hashState(state);
        if (updateOptions.commit === true) {
            const result = await options.persistence.createRevision({
                repoId: options.repoId,
                branchName,
                schemaVersion: options.schemaVersion,
                graphIdentity,
                state,
                stateHash,
                ...(updateOptions.message === undefined
                    ? {}
                    : { message: updateOptions.message }),
                ...(updateOptions.author === undefined
                    ? {}
                    : { author: updateOptions.author }),
                ...(updateOptions.expectedHeadHash === undefined
                    ? {}
                    : { expectedHeadHash: updateOptions.expectedHeadHash })
            });
            return {
                head: result.head,
                revision: result.revision,
                createdRevision: result.created
            };
        }
        const result = await options.persistence.writeHead({
            repoId: options.repoId,
            branchName,
            state,
            stateHash,
            ...(updateOptions.author === undefined
                ? {}
                : { author: updateOptions.author }),
            ...(updateOptions.expectedHeadHash === undefined
                ? {}
                : { expectedHeadHash: updateOptions.expectedHeadHash }),
            ...(updateOptions.concurrency === undefined
                ? {}
                : { concurrency: updateOptions.concurrency })
        });
        return {
            head: result.head,
            createdRevision: false
        };
    }
    function withDefaultExpectedHeadHash(updateOptions, head) {
        if (updateOptions.expectedHeadHash !== undefined ||
            updateOptions.concurrency === "last-write-wins") {
            return updateOptions;
        }
        return {
            ...updateOptions,
            expectedHeadHash: head.stateHash
        };
    }
    const repository = {
        getGraphIdentity() {
            return graphIdentity;
        },
        async assertCompatibleGraph(compatibilityOptions = {}) {
            let sourceIdentity;
            if (compatibilityOptions.revision !== undefined) {
                const storedRevision = await options.persistence.readRevision({
                    repoId: options.repoId,
                    revision: compatibilityOptions.revision
                });
                if (storedRevision === null) {
                    throw new RevisionNotFoundError(`Revision "${compatibilityOptions.revision}" was not found.`);
                }
                sourceIdentity = {
                    graphVersion: storedRevision.revision.graphVersion,
                    schemaFingerprint: storedRevision.revision.schemaFingerprint,
                    schemaFingerprintAlgorithm: storedRevision.revision.schemaFingerprintAlgorithm
                };
            }
            else {
                const branchName = resolveBranch(compatibilityOptions.branch);
                const head = await options.persistence.getHead({
                    repoId: options.repoId,
                    branchName
                });
                if (head === null) {
                    throw new BranchNotFoundError(`HEAD for branch "${branchName}" was not found.`);
                }
                sourceIdentity = await readHeadGraphIdentity(head);
            }
            if (sourceIdentity.schemaFingerprint === graphIdentity.schemaFingerprint) {
                return {
                    status: "compatible",
                    graphVersion: sourceIdentity.graphVersion,
                    schemaFingerprint: sourceIdentity.schemaFingerprint
                };
            }
            if (hasMigrationPath(sourceIdentity.graphVersion, graphIdentity.graphVersion)) {
                return {
                    status: "migration-required",
                    fromGraphVersion: sourceIdentity.graphVersion,
                    toGraphVersion: graphIdentity.graphVersion,
                    fromSchemaFingerprint: sourceIdentity.schemaFingerprint,
                    toSchemaFingerprint: graphIdentity.schemaFingerprint
                };
            }
            return {
                status: "incompatible",
                reason: "Schema fingerprints differ and no migration path is available.",
                fromGraphVersion: sourceIdentity.graphVersion,
                toGraphVersion: graphIdentity.graphVersion,
                fromSchemaFingerprint: sourceIdentity.schemaFingerprint,
                toSchemaFingerprint: graphIdentity.schemaFingerprint
            };
        },
        async init(initOptions) {
            activeBranch = initOptions.branch ?? defaultBranch;
            const initialState = validateState(initOptions.initialState);
            const stateHash = await hashState(initialState);
            const result = await options.persistence.createRepo({
                repoId: options.repoId,
                schemaVersion: options.schemaVersion,
                graphVersion,
                schemaFingerprint: graphIdentity.schemaFingerprint,
                schemaFingerprintAlgorithm: graphIdentity.schemaFingerprintAlgorithm,
                defaultBranch: activeBranch,
                storageMode: "snapshot",
                initialState,
                stateHash,
                commit: initOptions.commit !== false,
                ...(initOptions.message === undefined
                    ? {}
                    : { message: initOptions.message }),
                ...(initOptions.author === undefined ? {} : { author: initOptions.author })
            });
            return {
                head: result.head,
                ...(result.revision === undefined ? {} : { revision: result.revision })
            };
        },
        async getHead(getHeadOptions = {}) {
            const branchName = resolveBranch(getHeadOptions.branch);
            const head = await options.persistence.getHead({
                repoId: options.repoId,
                branchName
            });
            if (head === null) {
                throw new BranchNotFoundError(`HEAD for branch "${branchName}" was not found.`);
            }
            return validateHead(head, getHeadOptions);
        },
        watchHead(callback, watchOptions = {}) {
            if (options.persistence.subscribeHead === undefined) {
                throw new PersistenceError("Persistence adapter does not support watchHead.");
            }
            const branchName = resolveBranch(watchOptions.branch);
            return options.persistence.subscribeHead({
                repoId: options.repoId,
                branchName
            }, head => {
                void validateHead(head, watchOptions).then(callback);
            });
        },
        async update(updater, updateOptions = {}) {
            const current = await repository.getHead(updateOptions.branch === undefined
                ? {}
                : { branch: updateOptions.branch });
            return writeUpdate(updater(cloneJson(current.state)), withDefaultExpectedHeadHash(updateOptions, current));
        },
        async edit(recipe, updateOptions = {}) {
            const current = await repository.getHead(updateOptions.branch === undefined
                ? {}
                : { branch: updateOptions.branch });
            const draft = cloneJson(current.state);
            recipe(draft);
            return writeUpdate(draft, withDefaultExpectedHeadHash(updateOptions, current));
        },
        async commit(commitOptions = {}) {
            const branchName = resolveBranch(commitOptions.branch);
            const head = await repository.getHead({ branch: branchName });
            const result = await options.persistence.createRevision({
                repoId: options.repoId,
                branchName,
                schemaVersion: options.schemaVersion,
                graphIdentity,
                state: head.state,
                stateHash: head.stateHash,
                ...(commitOptions.message === undefined
                    ? {}
                    : { message: commitOptions.message }),
                ...(commitOptions.author === undefined
                    ? {}
                    : { author: commitOptions.author }),
                ...(commitOptions.allowEmpty === undefined
                    ? {}
                    : { allowEmpty: commitOptions.allowEmpty }),
                expectedHeadHash: commitOptions.expectedHeadHash ?? head.stateHash
            });
            return {
                head: result.head,
                revision: result.revision,
                created: result.created
            };
        },
        async readRevision(revision, readRevisionOptions = {}) {
            const storedRevision = await options.persistence.readRevision({
                repoId: options.repoId,
                revision
            });
            if (storedRevision === null) {
                throw new RevisionNotFoundError(`Revision "${revision}" was not found.`);
            }
            return cloneJson(await validateStoredRevision(storedRevision.revision, storedRevision.state, readRevisionOptions));
        },
        async migrateHead(migrateOptions = {}) {
            const branchName = resolveBranch(migrateOptions.branch);
            const rawHead = await options.persistence.getHead({
                repoId: options.repoId,
                branchName
            });
            if (rawHead === null) {
                throw new BranchNotFoundError(`HEAD for branch "${branchName}" was not found.`);
            }
            const sourceGraphIdentity = await readHeadGraphIdentity(rawHead);
            const targetGraphVersion = migrateOptions.to ?? graphVersion;
            assertCurrentTarget(targetGraphVersion);
            const migratedHead = await validateHead(rawHead, {
                branch: branchName,
                migrateTo: targetGraphVersion
            });
            const result = await options.persistence.createRevision({
                repoId: options.repoId,
                branchName,
                schemaVersion: options.schemaVersion,
                graphIdentity,
                state: migratedHead.state,
                stateHash: migratedHead.stateHash,
                message: migrateOptions.message ??
                    `Migrate graph ${sourceGraphIdentity.graphVersion} -> ${targetGraphVersion}`,
                ...(migrateOptions.author === undefined
                    ? {}
                    : { author: migrateOptions.author }),
                allowEmpty: migrateOptions.allowEmpty ??
                    (sourceGraphIdentity.graphVersion !== targetGraphVersion ||
                        sourceGraphIdentity.schemaFingerprint !== graphIdentity.schemaFingerprint),
                expectedHeadHash: migrateOptions.expectedHeadHash ?? rawHead.stateHash
            });
            return {
                head: result.head,
                revision: result.revision,
                created: result.created
            };
        },
        async listRevisions(listOptions = {}) {
            return options.persistence.listRevisions({
                repoId: options.repoId,
                ...(listOptions.branch === undefined
                    ? {}
                    : { branchName: listOptions.branch }),
                ...(listOptions.limit === undefined ? {} : { limit: listOptions.limit }),
                ...(listOptions.after === undefined ? {} : { after: listOptions.after }),
                ...(listOptions.order === undefined ? {} : { order: listOptions.order })
            });
        },
        watchRevisions(callback, watchOptions = {}) {
            if (options.persistence.subscribeRevisions === undefined) {
                throw new PersistenceError("Persistence adapter does not support watchRevisions.");
            }
            return options.persistence.subscribeRevisions({
                repoId: options.repoId,
                ...(watchOptions.branch === undefined
                    ? {}
                    : { branchName: watchOptions.branch }),
                ...(watchOptions.limit === undefined ? {} : { limit: watchOptions.limit }),
                ...(watchOptions.after === undefined ? {} : { after: watchOptions.after }),
                ...(watchOptions.order === undefined ? {} : { order: watchOptions.order })
            }, revisions => {
                callback([...revisions]);
            });
        },
        async tag(name, tagOptions = {}) {
            const branchName = resolveBranch(tagOptions.branch);
            let revision = tagOptions.revision;
            if (revision === undefined || revision === "HEAD") {
                const head = await repository.getHead({ branch: branchName });
                if (head.status === "dirty") {
                    if (tagOptions.createRevisionIfDirty === false) {
                        throw new DirtyHeadError("Cannot tag a dirty HEAD when createRevisionIfDirty is false.");
                    }
                    const commitResult = await repository.commit({
                        branch: branchName,
                        message: `Create revision for tag ${name}`,
                        ...(tagOptions.author === undefined
                            ? {}
                            : { author: tagOptions.author })
                    });
                    revision = commitResult.revision.revision;
                }
                else {
                    if (head.headRevision === null) {
                        throw new RevisionNotFoundError("Cannot tag an empty HEAD.");
                    }
                    revision = head.headRevision;
                }
            }
            if (revision === undefined || revision === null) {
                throw new RevisionNotFoundError("Cannot tag an empty HEAD.");
            }
            return options.persistence.createTag({
                repoId: options.repoId,
                name,
                revision,
                branchName,
                ...(tagOptions.annotation === undefined
                    ? {}
                    : { annotation: tagOptions.annotation }),
                ...(tagOptions.author === undefined ? {} : { author: tagOptions.author }),
                ...(tagOptions.overwrite === undefined
                    ? {}
                    : { overwrite: tagOptions.overwrite })
            });
        },
        async listTags() {
            return options.persistence.listTags({
                repoId: options.repoId
            });
        },
        async deleteTag(name, deleteTagOptions = {}) {
            return options.persistence.deleteTag({
                repoId: options.repoId,
                name,
                missing: deleteTagOptions.missing ?? "throw",
                ...(deleteTagOptions.expectedRevision === undefined
                    ? {}
                    : { expectedRevision: deleteTagOptions.expectedRevision }),
                ...(deleteTagOptions.author === undefined
                    ? {}
                    : { author: deleteTagOptions.author })
            });
        },
        async planGarbageCollection(planOptions = {}) {
            const normalizedOptions = normalizeGarbageCollectionPlanOptions(planOptions);
            if (options.persistence.planGarbageCollection !== undefined) {
                return options.persistence.planGarbageCollection({
                    repoId: options.repoId,
                    ...(planOptions.beforeRevision === undefined
                        ? {}
                        : { beforeRevision: planOptions.beforeRevision }),
                    keepTagged: true,
                    keepBranchHeads: true,
                    keepDirtyBaseRevisions: true,
                    includeOrphanBlobs: normalizedOptions.includeOrphanBlobs,
                    protectRevisions: normalizedOptions.protectRevisions,
                    ...(normalizedOptions.maxRevisionsToDelete === null
                        ? {}
                        : { maxRevisionsToDelete: normalizedOptions.maxRevisionsToDelete }),
                    estimateStorage: normalizedOptions.estimateStorage
                });
            }
            return planGarbageCollectionFromRepository({
                repoId: options.repoId,
                persistence: options.persistence,
                options: normalizedOptions
            });
        },
        async runGarbageCollection(plan, runOptions = {}) {
            if (plan.repoId !== options.repoId) {
                throw new PersistenceError(`Garbage collection plan belongs to repository "${plan.repoId}", not "${options.repoId}".`);
            }
            if (options.persistence.runGarbageCollection === undefined) {
                throw new PersistenceError("Persistence adapter does not support runGarbageCollection.");
            }
            const recomputedPlan = await repository.planGarbageCollection(planOptionsFromRequiredOptions(plan.options));
            if (recomputedPlan.refsSnapshotHash !== plan.refsSnapshotHash) {
                throw new GarbageCollectionPlanStaleError("Garbage collection plan is stale. Recompute the plan before running it.");
            }
            return options.persistence.runGarbageCollection({
                repoId: options.repoId,
                plan: recomputedPlan,
                dryRun: runOptions.dryRun ?? false,
                recomputeBeforeRun: true,
                allowStalePlan: false,
                ...(runOptions.author === undefined ? {} : { author: runOptions.author })
            });
        },
        async listBranches() {
            return options.persistence.listBranches({
                repoId: options.repoId
            });
        },
        async createBranch(name, createBranchOptions) {
            const branch = await options.persistence.createBranch({
                repoId: options.repoId,
                name,
                from: createBranchOptions.from,
                sourceBranch: activeBranch,
                ...(createBranchOptions.checkout === undefined
                    ? {}
                    : { checkout: createBranchOptions.checkout }),
                ...(createBranchOptions.author === undefined
                    ? {}
                    : { author: createBranchOptions.author })
            });
            if (createBranchOptions.checkout === true) {
                activeBranch = name;
            }
            return branch;
        },
        async checkout(branch) {
            const head = await options.persistence.getHead({
                repoId: options.repoId,
                branchName: branch
            });
            if (head === null) {
                throw new BranchNotFoundError(`Branch "${branch}" was not found.`);
            }
            activeBranch = branch;
            return head;
        },
        async restore(revision, restoreOptions = {}) {
            const branchName = resolveBranch(restoreOptions.branch);
            const head = await repository.getHead({ branch: branchName });
            const state = await repository.readRevision(revision);
            return writeUpdate(state, {
                branch: branchName,
                ...(restoreOptions.commit === undefined
                    ? {}
                    : { commit: restoreOptions.commit }),
                ...(restoreOptions.message === undefined
                    ? {}
                    : { message: restoreOptions.message }),
                ...(restoreOptions.author === undefined
                    ? {}
                    : { author: restoreOptions.author }),
                expectedHeadHash: restoreOptions.expectedHeadHash ?? head.stateHash
            });
        },
        async resetBranch(branch, resetOptions) {
            if (resetOptions.mode !== "hard") {
                throw new PersistenceError('resetBranch only supports mode "hard".');
            }
            return options.persistence.resetBranch({
                repoId: options.repoId,
                branchName: branch,
                to: resetOptions.to,
                mode: resetOptions.mode,
                ...(resetOptions.author === undefined ? {} : { author: resetOptions.author }),
                ...(resetOptions.expectedHeadHash === undefined
                    ? {}
                    : { expectedHeadHash: resetOptions.expectedHeadHash })
            });
        }
    };
    const crudHelpers = createCrudHelpers(options.graph, repository);
    return {
        ...repository,
        singletons: crudHelpers.singletons,
        entities: crudHelpers.entities
    };
}
function normalizeGarbageCollectionPlanOptions(options) {
    return {
        beforeRevision: options.beforeRevision ?? null,
        keepTagged: true,
        keepBranchHeads: true,
        keepDirtyBaseRevisions: true,
        includeOrphanBlobs: options.includeOrphanBlobs ?? true,
        protectRevisions: [...(options.protectRevisions ?? [])].sort((left, right) => left - right),
        maxRevisionsToDelete: options.maxRevisionsToDelete ?? null,
        estimateStorage: options.estimateStorage ?? true
    };
}
function planOptionsFromRequiredOptions(options) {
    return {
        ...(options.beforeRevision === null
            ? {}
            : { beforeRevision: options.beforeRevision }),
        keepTagged: true,
        keepBranchHeads: true,
        keepDirtyBaseRevisions: true,
        includeOrphanBlobs: options.includeOrphanBlobs,
        protectRevisions: options.protectRevisions,
        ...(options.maxRevisionsToDelete === null
            ? {}
            : { maxRevisionsToDelete: options.maxRevisionsToDelete }),
        estimateStorage: options.estimateStorage
    };
}
async function planGarbageCollectionFromRepository(input) {
    const revisions = (await input.persistence.listRevisions({
        repoId: input.repoId,
        order: "asc"
    })).sort((left, right) => left.revision - right.revision);
    const tags = (await input.persistence.listTags({ repoId: input.repoId })).sort((left, right) => left.name.localeCompare(right.name));
    const branches = (await input.persistence.listBranches({ repoId: input.repoId })).sort((left, right) => left.name.localeCompare(right.name));
    const revisionMap = new Map(revisions.map(revision => [revision.revision, revision]));
    const protectedReasons = new Map();
    const blockedReasons = new Map();
    const latestRevision = revisions.length === 0 ? null : revisions[revisions.length - 1]?.revision ?? null;
    const refsSnapshot = buildGarbageCollectionRefsSnapshot({
        tags,
        branches,
        latestRevision
    });
    const refsSnapshotHash = await hashState(refsSnapshot);
    function addProtected(revision, reason) {
        const reasons = protectedReasons.get(revision) ?? new Set();
        reasons.add(reason);
        protectedReasons.set(revision, reasons);
        addBlocked(revision, reason);
    }
    function addBlocked(revision, reason) {
        const reasons = blockedReasons.get(revision) ?? new Set();
        reasons.add(reason);
        blockedReasons.set(revision, reasons);
    }
    const protectedRoots = [];
    for (const tag of tags) {
        addProtected(tag.revision, "tagged");
        protectedRoots.push(tag.revision);
    }
    for (const branch of branches) {
        if (branch.headRevision !== null) {
            addProtected(branch.headRevision, "branch-head");
            protectedRoots.push(branch.headRevision);
        }
        if (branch.baseRevision !== null) {
            addProtected(branch.baseRevision, "branch-base-revision");
            protectedRoots.push(branch.baseRevision);
            if (branch.status === "dirty") {
                addProtected(branch.baseRevision, "dirty-base-revision");
            }
        }
    }
    for (const revision of input.options.protectRevisions) {
        addProtected(revision, "explicitly-protected");
        protectedRoots.push(revision);
    }
    for (const revision of protectedRoots) {
        if (!revisionMap.has(revision)) {
            addBlocked(revision, "missing-metadata");
            continue;
        }
        markAncestorRevisions({
            startRevision: revision,
            revisionMap,
            onAncestor: ancestor => {
                addProtected(ancestor, "ancestor-of-protected-revision");
            },
            onMissingParent: missingParent => {
                addBlocked(missingParent, "missing-metadata");
            },
            onUnknownParent: child => {
                addBlocked(child, "unknown-parent");
            }
        });
    }
    for (const revision of revisions) {
        if (protectedReasons.has(revision.revision)) {
            continue;
        }
        if (input.options.beforeRevision !== null &&
            revision.revision >= input.options.beforeRevision) {
            addBlocked(revision.revision, "after-before-revision-threshold");
            markAncestorRevisions({
                startRevision: revision.revision,
                revisionMap,
                onAncestor: ancestor => {
                    if (!protectedReasons.has(ancestor)) {
                        addBlocked(ancestor, "ancestor-of-protected-revision");
                    }
                },
                onMissingParent: missingParent => {
                    addBlocked(missingParent, "missing-metadata");
                },
                onUnknownParent: child => {
                    addBlocked(child, "unknown-parent");
                }
            });
        }
    }
    const deletableRevisions = [];
    for (const revision of revisions) {
        if (blockedReasons.has(revision.revision)) {
            continue;
        }
        if (revision.parentRevision !== null &&
            !revisionMap.has(revision.parentRevision)) {
            addBlocked(revision.revision, "unknown-parent");
            continue;
        }
        if (revision.snapshotRef === undefined) {
            addBlocked(revision.revision, "missing-metadata");
            continue;
        }
        const estimatedStorage = await estimateRevisionStorage({
            repoId: input.repoId,
            persistence: input.persistence,
            revision,
            estimateStorage: input.options.estimateStorage
        });
        if (estimatedStorage === null) {
            addBlocked(revision.revision, "missing-metadata");
            continue;
        }
        deletableRevisions.push({
            revision: revision.revision,
            parentRevision: revision.parentRevision,
            branchName: revision.branchName,
            stateHash: revision.stateHash,
            snapshotBlobRef: revision.snapshotRef,
            estimatedStorage
        });
    }
    const limitedDeletableRevisions = input.options.maxRevisionsToDelete === null
        ? deletableRevisions
        : deletableRevisions.slice(0, input.options.maxRevisionsToDelete);
    const orphanBlobs = [];
    const estimatedFreedStorage = sumStorageEstimates([
        ...limitedDeletableRevisions.map(revision => revision.estimatedStorage),
        ...orphanBlobs.map(blob => blob.estimatedStorage)
    ]);
    return {
        planId: `gc:${refsSnapshotHash}:${limitedDeletableRevisions
            .map(revision => revision.revision)
            .join(",")}`,
        repoId: input.repoId,
        strategy: "unreachable-snapshots-v1",
        createdAt: new Date().toISOString(),
        options: input.options,
        protectedRevisions: mapReasonRecords(protectedReasons),
        deletableRevisions: limitedDeletableRevisions,
        blockedRevisions: mapReasonRecords(blockedReasons),
        orphanBlobs,
        estimatedFreedStorage,
        refsSnapshot,
        refsSnapshotHash
    };
}
function buildGarbageCollectionRefsSnapshot(input) {
    return {
        tags: input.tags.map(tag => ({
            name: tag.name,
            revision: tag.revision
        })),
        branches: input.branches.map(branch => ({
            name: branch.name,
            headRevision: branch.headRevision,
            baseRevision: branch.baseRevision,
            status: branch.status,
            ...(branch.headBlobRef === undefined
                ? {}
                : { headBlobRef: branch.headBlobRef })
        })),
        latestRevision: input.latestRevision
    };
}
function markAncestorRevisions(input) {
    const visited = new Set();
    let currentRevision = input.startRevision;
    while (!visited.has(currentRevision)) {
        visited.add(currentRevision);
        const current = input.revisionMap.get(currentRevision);
        if (current === undefined) {
            input.onMissingParent(currentRevision);
            return;
        }
        if (current.parentRevision === null) {
            return;
        }
        const parent = input.revisionMap.get(current.parentRevision);
        if (parent === undefined) {
            input.onMissingParent(current.parentRevision);
            input.onUnknownParent(current.revision);
            return;
        }
        input.onAncestor(parent.revision);
        currentRevision = parent.revision;
    }
    input.onUnknownParent(currentRevision);
}
async function estimateRevisionStorage(input) {
    if (!input.estimateStorage) {
        return zeroStorageEstimate();
    }
    const storedRevision = await input.persistence.readRevision({
        repoId: input.repoId,
        revision: input.revision.revision
    });
    if (storedRevision === null) {
        return null;
    }
    return {
        bytes: byteLength(stableStringify(storedRevision.revision)) +
            byteLength(stableStringify(storedRevision.state)),
        documents: 1,
        blobs: input.revision.snapshotRef === undefined ? 0 : 1
    };
}
function zeroStorageEstimate() {
    return {
        bytes: 0,
        documents: 0,
        blobs: 0
    };
}
function sumStorageEstimates(estimates) {
    return estimates.reduce((total, estimate) => ({
        bytes: total.bytes + estimate.bytes,
        documents: total.documents + estimate.documents,
        blobs: total.blobs + estimate.blobs
    }), zeroStorageEstimate());
}
function byteLength(value) {
    return new TextEncoder().encode(value).byteLength;
}
function mapReasonRecords(reasons) {
    return Array.from(reasons.entries())
        .sort(([left], [right]) => left - right)
        .map(([revision, reasonSet]) => ({
        revision,
        reasons: Array.from(reasonSet).sort()
    }));
}
function createCrudHelpers(graph, repository) {
    const singletons = {};
    const entities = {};
    for (const [entryName, entry] of Object.entries(graph.entries)) {
        if (entry.kind === "singleton") {
            singletons[entryName] = createSingletonCrudHelper(entryName, repository);
            continue;
        }
        entities[entryName] = createEntityCrudHelper(entryName, repository);
    }
    return {
        singletons: singletons,
        entities: entities
    };
}
function createSingletonCrudHelper(entryName, repository) {
    return {
        async get(options = {}) {
            const head = await repository.getHead(options);
            return cloneJson(readStateEntry(head.state, entryName));
        },
        async set(value, options = {}) {
            return repository.update(current => writeStateEntry(current, entryName, value), options);
        },
        async update(updater, options = {}) {
            return repository.update(current => {
                const value = cloneJson(readStateEntry(current, entryName));
                return writeStateEntry(current, entryName, updater(value));
            }, options);
        }
    };
}
function createEntityCrudHelper(entryName, repository) {
    return {
        async list(options = {}) {
            const head = await repository.getHead(options);
            return cloneJson(readCollectionEntry(head.state, entryName));
        },
        async get(id, options = {}) {
            const head = await repository.getHead(options);
            const collection = readCollectionEntry(head.state, entryName);
            if (!Object.hasOwn(collection, id)) {
                return null;
            }
            return cloneJson(collection[id]);
        },
        async create(id, value, options = {}) {
            return repository.update(current => {
                const collection = readCollectionEntry(current, entryName);
                if (Object.hasOwn(collection, id)) {
                    throw new EntityAlreadyExistsError(`Entity "${id}" already exists in collection "${entryName}".`);
                }
                return writeStateEntry(current, entryName, {
                    ...collection,
                    [id]: value
                });
            }, options);
        },
        async update(id, updater, options = {}) {
            return repository.update(current => {
                const collection = readCollectionEntry(current, entryName);
                if (!Object.hasOwn(collection, id)) {
                    throw new EntityNotFoundError(`Entity "${id}" was not found in collection "${entryName}".`);
                }
                return writeStateEntry(current, entryName, {
                    ...collection,
                    [id]: updater(cloneJson(collection[id]))
                });
            }, options);
        },
        async delete(id, options = {}) {
            return repository.update(current => {
                const collection = readCollectionEntry(current, entryName);
                if (!Object.hasOwn(collection, id)) {
                    throw new EntityNotFoundError(`Entity "${id}" was not found in collection "${entryName}".`);
                }
                const nextCollection = Object.fromEntries(Object.entries(collection).filter(([entityId]) => entityId !== id));
                return writeStateEntry(current, entryName, nextCollection);
            }, options);
        }
    };
}
function readStateEntry(state, entryName) {
    return state[entryName];
}
function writeStateEntry(state, entryName, value) {
    return {
        ...state,
        [entryName]: value
    };
}
function readCollectionEntry(state, entryName) {
    const value = readStateEntry(state, entryName);
    if (!isRecord(value)) {
        throw new EntityNotFoundError(`Collection "${entryName}" was not found on the current state.`);
    }
    return value;
}
function isRecord(value) {
    if (value === null || typeof value !== "object" || Array.isArray(value)) {
        return false;
    }
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
}
//# sourceMappingURL=repository.js.map